import React, { useRef, useState, useEffect } from 'react'

import { createPortal } from 'react-dom'
import { FiSettings, FiX, FiInfo } from 'react-icons/fi'

import DataSourceDropdown from '@/components/ui/data-source/DataSourceDropdown'
import { useApi, useDashboard, useCanSelectDataSource } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getInsightDefinition, type InsightKey } from '@/insights/insights'
import type { Asset, InsightConfigurationType, InsightDefinition, InsightStatus } from '@/types/dashboard-types'
import { DATA_SOURCE_KEY, DEFAULT_DATA_SOURCE, type DataSource } from '@/types/data-source'

import { ConfigControls } from './ConfigControls'

interface InsightConfigPanelProps {
  isOpen: boolean
  onClose: (insightId?: string) => void
  configuration: InsightConfigurationType
}

const InsightConfigPanelContent: React.FC<InsightConfigPanelProps> = ({ isOpen, onClose, configuration }) => {
  const dashboardContext = useDashboard()
  const { companies } = useCompany()
  const { api } = useApi()
  const [dashboardAssetName, setDashboardAssetName] = useState<string>('')

  // Resolve asset name for dashboard-level asset
  const canSelectDataSource = useCanSelectDataSource()
  const dashboardParams = dashboardContext.currentDashboard?.parameters || {}
  useEffect(() => {
    if (!api) {
      return
    }
    if (!dashboardParams.assetId || !dashboardParams.companyId) {
      setDashboardAssetName('')
      return
    }
    const fn = async () => {
      const response = await api.get<Asset[]>({
        path: `/assets?companyId=${dashboardParams.companyId}&skip=0&take=10000&deleted=false`,
        origin: 'InsightConfigPanel',
        expiresIn: '1h',
      })
      if (!response.error && response.data) {
        const asset = response.data.find((a) => String(a.id) === String(dashboardParams.assetId))
        setDashboardAssetName(asset?.name || String(dashboardParams.assetId))
      }
    }
    fn()
  }, [dashboardParams.assetId, dashboardParams.companyId, api])
  // Goofy way to let typescript know we are dealing with a specific Insight
  const _definition = getInsightDefinition(configuration.slug as InsightKey)
  type DataType = Exclude<Awaited<ReturnType<typeof _definition.load>>, InsightStatus>
  type ParamsType = Parameters<typeof _definition.configuration.isValid>[0]
  const definition = _definition as InsightDefinition<ParamsType, DataType>
  const [parameters, setParameters] = React.useState<ParamsType>({
    ...(configuration.parameters as ParamsType),
    title: configuration.title,
  })

  const [hasShownTitlePrompt, setHasShownTitlePrompt] = useState(false)

  const originalParamsRef = useRef<ParamsType & { title: string }>({
    ...(configuration.parameters as ParamsType),
    title: configuration.title,
  })

  const currentInsightIdRef = useRef<string | null>(null)

  // update refs and reset state
  React.useEffect(() => {
    originalParamsRef.current = {
      ...(configuration.parameters as ParamsType),
      title: configuration.title,
    }

    if (currentInsightIdRef.current !== configuration.id) {
      currentInsightIdRef.current = configuration.id!
      setHasShownTitlePrompt(false)
    }

    setParameters({
      ...(configuration.parameters as ParamsType),
      title: configuration.title,
    })
  }, [configuration.id, isOpen, configuration.title, configuration.parameters])

  //prompt state management
  React.useEffect(() => {
    const { title, ...actualParameters } = parameters as ParamsType & { title?: string }
    const { title: _origTitle, ...originalActualParams } = originalParamsRef.current

    const paramsChanged = JSON.stringify(actualParameters) !== JSON.stringify(originalActualParams)
    const titleChanged = title !== originalParamsRef.current.title

    if (paramsChanged && !titleChanged && !hasShownTitlePrompt) {
      setHasShownTitlePrompt(true)
      return
    }

    if (hasShownTitlePrompt && (!paramsChanged || titleChanged)) {
      setHasShownTitlePrompt(false)
    }
  }, [parameters, hasShownTitlePrompt])

  const handleSave = React.useCallback(() => {
    const { title, ...actualParameters } = parameters as ParamsType & { title?: string }

    dashboardContext.updateInsight({
      dashboardSlug: dashboardContext.currentDashboard.slug,
      id: configuration.id!,
      insightConfiguration: {
        ...configuration,
        title: title || definition.name,
        parameters: actualParameters,
      },
    })
    onClose(configuration.id)
  }, [configuration, parameters])

  if (!isOpen) return null

  const isValid = definition?.configuration.isValid(parameters)

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={() => onClose()} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-3/4 md:w-7/12 lg:w-1/2 xl:w-2/5 2xl:w-1/3 max-w-[500px] bg-base-100 shadow-xl border-l border-base-300">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-base-300">
            <h2 className="text-xl font-semibold flex items-start gap-2">
              <FiSettings className="w-5 h-5 mt-1" />
              <div>
                {definition ? `Edit: ${configuration.title}` : 'Bad Insight: Remove'}
                <div className="font-normal text-sm">{definition?.name || ''}</div>
              </div>
            </h2>
            <button onClick={() => onClose()} className="btn btn-ghost btn-sm btn-square">
              <FiX className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-4">
              <label className="block text-base-content mb-2">Title</label>
              <input
                type="text"
                className="input input-bordered text-base border-base-300 w-full focus:outline-none focus:border-primary/30"
                value={parameters.title as string}
                onChange={(e) => setParameters({ ...parameters, title: e.target.value })}
                placeholder={definition?.name}
                onFocus={(e) => e.target.select()}
              />
            </div>

            {canSelectDataSource ? (
              <div className="mb-6">
                <DataSourceDropdown
                  label="Data Source"
                  value={(parameters[DATA_SOURCE_KEY] as DataSource | undefined) ?? DEFAULT_DATA_SOURCE}
                  onChange={(value) => setParameters({ ...parameters, [DATA_SOURCE_KEY]: value })}
                />
              </div>
            ) : null}

            {(definition?.configuration.controls || []).map((control) => {
              const dashboardParams = dashboardContext.currentDashboard?.parameters || {}
              const disabledFieldLabels: Record<string, string> = {}
              if (dashboardParams.companyId) {
                const company = companies.find((c) => String(c.id) === String(dashboardParams.companyId))
                disabledFieldLabels.companyId = company?.name || String(dashboardParams.companyId)
              }
              if (dashboardParams.assetId) {
                disabledFieldLabels.assetId = dashboardAssetName || String(dashboardParams.assetId)
              }
              if (dashboardParams.date) {
                if (dashboardParams.date === 'custom') {
                  const f = dashboardParams.from ? String(dashboardParams.from).slice(0, 10) : '...'
                  const t = dashboardParams.to ? String(dashboardParams.to).slice(0, 10) : '...'
                  disabledFieldLabels.date = `Custom: ${f} – ${t}`
                  disabledFieldLabels.from = f
                  disabledFieldLabels.to = t
                } else {
                  const opt = DateOptions.find((o) => o.value === dashboardParams.date)
                  disabledFieldLabels.date = opt?.label || String(dashboardParams.date)
                }
              }
              return (
                <div key={control.id} className="mb-6">
                  <ConfigControls
                    control={control}
                    parameters={parameters}
                    disabledFieldLabels={disabledFieldLabels}
                    onChange={(params) => {
                      let newParams = { ...params }
                      if (definition.configuration.transform) {
                        newParams = definition.configuration.transform({ parameters: params, field: control.id })
                      }
                      setParameters(newParams)
                    }}
                  />
                </div>
              )
            })}
          </div>
          <div className="border-t border-base-300 p-4 flex flex-col gap-4">
            {hasShownTitlePrompt && (
              <div className="text-sm bg-info/10 p-2 border border-info rounded-md flex justify-between items-center mt-2 text-info transition-all duration-150">
                <FiInfo className="text-info text-lg" />
                Configuration changed - ensure title matches new configuration
              </div>
            )}
            <div className=" flex gap-2 justify-between">
              <button
                className="border border-red-700 text-red-700 hover:bg-red-200 px-4 py-2 rounded cursor-pointer"
                onClick={() => dashboardContext.removeInsight({ id: configuration.id! })}
              >
                Remove Insight
              </button>
              <div className="flex justify-between items-center gap-4">
                <button onClick={() => onClose()} className="btn btn-ghost">
                  Cancel
                </button>

                <button
                  onClick={handleSave}
                  className="btn btn-primary"
                  disabled={isValid !== true}
                  title={typeof isValid === 'string' ? isValid : undefined}
                >
                  Apply Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const InsightConfigPanel: React.FC<InsightConfigPanelProps> = (props) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Don't render anything if not open
  if (!props.isOpen) return null

  // Use portal to render outside of any grid container's stacking context
  if (!mounted) return null

  return createPortal(<InsightConfigPanelContent {...props} />, document.body)
}
